import { Link } from 'react-router-dom';
import SEOMeta from '@/components/base/SEOMeta';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黄瓜影院',
    url: 'https://huanggua.tv/',
    description: '黄瓜影院是您最佳的在线影视平台，提供海量高清电影、电视剧、综艺节目免费在线观看。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@huanggua.tv',
      availableLanguage: 'Chinese',
    },
    sameAs: ['https://weibo.com/huanggua', 'https://space.bilibili.com/huanggua'],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://huanggua.tv/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '黄瓜影院是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '黄瓜影院是一个专业的在线影视平台，致力于为用户提供海量高清电影、电视剧、综艺节目和动漫的免费在线观看服务。我们收录最新最热门的影视资源，支持多线路流畅播放。',
        },
      },
      {
        '@type': 'Question',
        name: '黄瓜影院是否需要付费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要！黄瓜影院所有内容完全免费，用户可以随时在线观看，无需注册或付费。',
        },
      },
      {
        '@type': 'Question',
        name: '黄瓜影院如何保证内容质量？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '黄瓜影院拥有专业的内容审核团队，所有收录的影视资源都经过严格的质量把关，确保提供高清画质和流畅的观看体验。同时我们每天更新最新影视资源。',
        },
      },
    ],
  };

  return (
    <>
      <SEOMeta
        title="关于我们 - 黄瓜影院"
        description="黄瓜影院是一个专业的在线影视平台，致力于为用户提供海量高清电影、电视剧、综艺节目和动漫免费在线观看服务。了解黄瓜影院的品牌故事、发展历程和联系方式。"
        keywords="黄瓜影院关于我们,黄瓜影院品牌介绍,黄瓜影院联系方式,在线影院平台介绍"
        canonicalUrl="https://huanggua.tv/about"
        schema={{ ...aboutSchema, ...breadcrumbSchema, ...faqSchema }}
      />
      <div className="pt-20 pb-12 md:pb-16">
        <div className="max-w-[900px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium">关于我们</span>
          </nav>

          <section className="mb-16">
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6">
              关于黄瓜影院
            </h1>
            <div className="rounded-xl overflow-hidden mb-8 bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20home%20cinema%20room%20with%20large%20screen%20and%20comfortable%20seating%2C%20warm%20ambient%20red%20and%20golden%20lighting%2C%20cozy%20luxurious%20atmosphere%2C%20professional%20interior%20design%2C%20no%20text%2C%20no%20people&width=1200&height=500&seq=about-hero-01&orientation=landscape"
                alt="黄瓜影院 - 关于我们"
                className="w-full aspect-[2.4/1] object-cover"
              />
            </div>
            <div className="prose prose-lg max-w-none text-foreground-700 leading-relaxed space-y-5">
              <p>
                <strong>黄瓜影院</strong>（huanggua.tv）成立于2020年，是一个专业的在线影视资源平台。我们致力于为广大影视爱好者提供海量高清电影、电视剧、综艺节目和动漫的免费在线观看服务。
              </p>
              <p>
                作为国内领先的免费影视平台之一，<strong>黄瓜影院</strong>以用户需求为导向，持续优化观影体验。我们收录了超过5000部优质影视资源，覆盖科幻、动作、爱情、悬疑、喜剧、动画等多种类型，满足不同用户的观影需求。
              </p>
              <p>
                <strong>黄瓜影院</strong>始终坚持"免费、高清、流畅"的服务理念。无需注册登录，用户即可在线观看海量影视内容。我们支持多线路智能切换，确保每一位用户都能享受流畅的观影体验。
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
              品牌口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <div className="bg-background-100 rounded-xl p-6">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <span key={s} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                  "黄瓜影院的资源太全了！我想看的电影和电视剧这里都有，画质也很清晰，最重要的是完全免费不用注册。"
                </p>
                <p className="text-xs text-foreground-500">—— 资深影迷 张先生</p>
              </div>
              <div className="bg-background-100 rounded-xl p-6">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <span key={s} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                  "更新速度超级快！热门剧集播出当天就能在黄瓜影院看到，播放也很流畅，已经推荐给身边所有朋友了。"
                </p>
                <p className="text-xs text-foreground-500">—— 追剧达人 李女士</p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 sm:col-span-2 lg:col-span-1">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <span key={s} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                  "国漫板块做得特别好！《凡人修仙传》《完美世界》这些热门国漫都能在黄瓜影院高清观看，国漫迷的福音。"
                </p>
                <p className="text-xs text-foreground-500">—— 动漫爱好者 小王</p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-background-100 rounded-xl p-6 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">
                    电子邮箱
                  </h3>
                  <p className="text-sm text-foreground-600">
                    contact@huanggua.tv
                  </p>
                </div>
              </div>
              <div className="bg-background-100 rounded-xl p-6 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">
                    官方微信
                  </h3>
                  <p className="text-sm text-foreground-600">
                    搜索关注：黄瓜影院官方
                  </p>
                </div>
              </div>
              <div className="bg-background-100 rounded-xl p-6 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                  <i className="ri-weibo-line text-lg"></i>
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">
                    官方微博
                  </h3>
                  <p className="text-sm text-foreground-600">
                    @黄瓜影院官方微博
                  </p>
                </div>
              </div>
              <div className="bg-background-100 rounded-xl p-6 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                  <i className="ri-bilibili-line text-lg"></i>
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">
                    哔哩哔哩
                  </h3>
                  <p className="text-sm text-foreground-600">
                    搜索关注：黄瓜影院官方
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-background-100 rounded-xl p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">
              常见问题
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                  Q: 黄瓜影院是什么？
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  A: 黄瓜影院是一个专业的在线影视平台，致力于为用户提供海量高清电影、电视剧、综艺节目和动漫的免费在线观看服务。我们收录最新最热门的影视资源，支持多线路流畅播放。
                </p>
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                  Q: 黄瓜影院是否需要付费？
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  A: 不需要！黄瓜影院所有内容完全免费，用户可以随时在线观看，无需注册或付费。我们始终秉承"免费、高清、流畅"的服务理念。
                </p>
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                  Q: 黄瓜影院如何保证内容质量？
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  A: 黄瓜影院拥有专业的内容审核团队，所有收录的影视资源都经过严格的质量把关，确保提供高清画质和流畅的观看体验。同时我们每天更新最新影视资源，确保内容的时效性。
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}